import CampaignFaqs from '@cohort/components-xps/components/CampaignFaqs';
import RewardCard from '@cohort/components-xps/components/cards/RewardCard';
import type {AboutJourneyType} from '@cohort/components-xps/components/journeys/AboutJourney';
import AboutJourney from '@cohort/components-xps/components/journeys/AboutJourney';
import {JourneySteps} from '@cohort/components-xps/components/journeys/JourneySteps';
import Title from '@cohort/components-xps/components/Title';
import {cn} from '@cohort/shared-frontend/utils/classNames';
import DetailsComponentWrapper from '@cohort/wallet/components/space/DetailsComponentWrapper';
import JourneyActionBtn from '@cohort/wallet/components/space/JourneyActionButton';
import JourneyStepCta from '@cohort/wallet/components/space/JourneyStepCta';
import JourneyStepsProgression from '@cohort/wallet/components/space/JourneyStepsProgression';
import {Spinner} from '@cohort/wallet/components/Spinner';
import {useJourneyCampaign} from '@cohort/wallet/hooks/api/Campaign';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useScreenSizeContext from '@cohort/wallet/hooks/useScreenSize';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import type {JourneyStepWDto} from '@cohort/wallet-schemas/journeySteps';
import {FileText} from '@phosphor-icons/react';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

const JourneyDetailPage = (): JSX.Element => {
  const {space} = useSpaceContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.journeyDetailPage'});
  const merchant = useMerchantContext();
  const {journeyId} = useParams();
  const {isMobile} = useScreenSizeContext();
  const {backgroundColor, hasDarkBg, accentColor} = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  const processingStepId = location.state?.processingStepId;

  const {data: campaign, isFetched} = useJourneyCampaign(
    merchant.id,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    journeyId!,
    journeyId !== undefined
  );

  // User is not whitelisted or the campaign does not exist
  if (isFetched && !campaign) {
    navigate('/space/not-found');
  }

  if (space === undefined || campaign === undefined) {
    return <Fragment />;
  }

  const aboutJourney = {
    animationFileKey: campaign.journey.animationFileKey,
    backgroundColorCode: backgroundColor,
    availableSupply: campaign.availableSupply,
    description: campaign.journey.description,
    hasDarkBg,
    imageFileKey: campaign.journey.imageFileKey,
    title: campaign.journey.title,
    isCampaignEnded: campaign.status === 'ended',
  } satisfies AboutJourneyType;

  const aboutJourneyComponent = (
    <AboutJourney aboutJourney={aboutJourney}>
      <Fragment>
        {campaign.journey.participation !== null && (
          <JourneyStepsProgression steps={campaign.journey.steps} />
        )}
        <JourneyActionBtn campaign={campaign} />
      </Fragment>
    </AboutJourney>
  );

  const stepCta = (step: JourneyStepWDto): JSX.Element => {
    if (processingStepId === step.id && !step.isCompleted) {
      return <Spinner color={accentColor} height="h-6" width="w-6" />;
    } else if (campaign.journey.participation !== null) {
      return (
        <JourneyStepCta
          journeyId={campaign.id}
          step={step}
          journeyParticipationId={campaign.journey.participation.id}
          isCampaignEnded={campaign.status === 'ended'}
        />
      );
    }
    return <Fragment />;
  };

  return (
    <SpacePage
      title={campaign.journey.title}
      leftPanel={aboutJourneyComponent}
      tracking={{
        namespace: 'journeys.detail',
      }}
    >
      <div className="space-y-6 md:space-y-10">
        {isMobile && aboutJourneyComponent}
        <section className="space-y-4">
          <Title content={t('stepsTitle')} />
          <JourneySteps.StepsList>
            {campaign.journey.steps.map(step => (
              <JourneySteps.StepCard
                key={step.id}
                step={step}
                hasDarkBg={hasDarkBg}
                isMobile={isMobile}
                isCampaignEnded={campaign.status === 'ended'}
                detailsComponent={
                  step.triggers.length === 1 &&
                  step.triggers[0] && <DetailsComponentWrapper trigger={step.triggers[0]} />
                }
              >
                {stepCta(step)}
              </JourneySteps.StepCard>
            ))}
          </JourneySteps.StepsList>
        </section>
        <section className="space-y-4">
          <Title content={t('rewardTitle')} />
          <RewardCard
            title={campaign.collection.name}
            perksTitles={campaign.perks.map(perk => perk.displayName)}
            imageFileKey={campaign.collection.imageFileKey}
            animationFileKey={campaign.collection.animationFileKey}
            perksCounter={campaign.perks.length}
            hasDarkBg={hasDarkBg}
            isMobile={isMobile}
          />
        </section>
        {(campaign.faqs.length > 0 || campaign.merchant.supportEmail !== null) && (
          <section className="space-y-4">
            <Title content={t('faqTitle')} />
            <CampaignFaqs
              faqs={campaign.faqs}
              accentColorCode={accentColor}
              hasDarkBg={hasDarkBg}
              supportEmail={campaign.merchant.supportEmail}
            />
          </section>
        )}
        {campaign.tosUrl && (
          <section className="space-y-4">
            <Title content={t('resourcesTitle')} />
            <div className="flex">
              <a
                href={campaign.tosUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary-500 hover:underline"
              >
                <div
                  className={cn(
                    'flex w-[180px] flex-col items-center justify-center gap-2 rounded-xl p-8',
                    hasDarkBg ? 'bg-white/8' : 'bg-black/4'
                  )}
                >
                  <div className={cn(hasDarkBg ? 'bg-white/8' : 'bg-black/4', 'rounded-xl p-2')}>
                    <FileText
                      size={32}
                      className={cn(hasDarkBg ? 'text-white/60' : 'text-black/60')}
                    />
                  </div>
                  <p className="text-center text-sm">{t('downloadFile')}</p>
                </div>
              </a>
            </div>
          </section>
        )}
      </div>
    </SpacePage>
  );
};

export default JourneyDetailPage;
